import { Badge, Table, Button, Tooltip } from "flowbite-react";
import { HiTrash } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { DataGridPagination } from "@/sharedTypes";
import TablePagination from "@/components/Pagination";
import { agentRoleLabels, agentStatusLabels, isCompanyAdminUser } from "@/constants/agent";
import { useCurrentUser } from "@/hooks/useCurrentUser";

interface ShiftAgentsTableProps {
  data: any[];
  pagination: DataGridPagination;
  onPageChange: (page: number) => void;
  loading: boolean;
  onRemoveAgent: (agentId: number) => void;
}

export default function ShiftAgentsTable({
  data,
  pagination,
  onPageChange,
  loading,
  onRemoveAgent
}: ShiftAgentsTableProps) {
  const { t } = useTranslation(["common", "workShift"]);
  const user = useCurrentUser();
  const isAdmin = isCompanyAdminUser(user);

  if (loading && data.length === 0) {
      // You might want a skeleton here, but simple text or spinner for now
      return <div className="p-4 text-center">Loading...</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="relative overflow-x-auto shadow-md sm:rounded-lg border border-gray-200 dark:border-gray-700">
        <Table hoverable striped className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
          <Table.Head className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <Table.HeadCell className="px-6 py-3 font-semibold tracking-wider">
               Username
            </Table.HeadCell>
            <Table.HeadCell className="px-6 py-3 font-semibold tracking-wider">
               Email
            </Table.HeadCell>
            <Table.HeadCell className="px-6 py-3 font-semibold tracking-wider text-center">
               Role
            </Table.HeadCell>
            <Table.HeadCell className="px-6 py-3 font-semibold tracking-wider text-center">
               Status
            </Table.HeadCell>
            {isAdmin && (
              <Table.HeadCell className="px-6 py-3 font-semibold tracking-wider text-center">
                {t("workShift:actions")}
              </Table.HeadCell>
            )}
          </Table.Head>
          <Table.Body className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {data?.length > 0 ? (
              data.map((agent: any) => {
                 const initials = agent.agent_username
                    ? agent.agent_username.split(" ").map((n: string) => n[0]).join("").toUpperCase().substring(0, 2)
                    : "??";
                 
                 const colors = [
                    "bg-blue-500",
                    "bg-green-500",
                    "bg-yellow-500",
                    "bg-red-500",
                    "bg-purple-500",
                 ];
                 const colorIndex = (agent.id || agent.agent_id || 0) % colors.length;

                 return (
                    <Table.Row key={agent.agent_id} className="bg-white dark:border-gray-700 dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-600">
                      <Table.Cell className="px-6 py-4 whitespace-nowrap font-medium text-gray-900 dark:text-white">
                         <div className="flex items-center space-x-3">
                             <div className={`flex items-center justify-center w-8 h-8 rounded-full text-white font-bold ${colors[colorIndex]}`}>
                                {initials}
                             </div>
                             <div>{agent.agent_username}</div>
                         </div>
                      </Table.Cell>
                      <Table.Cell className="px-6 py-4 whitespace-nowrap font-medium text-gray-900 dark:text-white">
                         {agent.agent_email}
                      </Table.Cell>
                      <Table.Cell className="px-6 py-4 whitespace-nowrap font-medium text-center">
                         <Badge color="gray" className="inline-flex">
                             {agentRoleLabels[agent.agent_role] || "Unknown"}
                         </Badge>
                      </Table.Cell>
                      <Table.Cell className="px-6 py-4 whitespace-nowrap font-medium text-center">
                          <Badge color={agent.agent_status === 13 ? "success" : "failure"} className="inline-flex">
                             {agentStatusLabels[agent.agent_status] || "Unknown"}
                          </Badge>
                      </Table.Cell>
                      {isAdmin && (
                        <Table.Cell className="px-6 py-4 whitespace-nowrap font-medium text-center">
                          <div className="flex justify-center">
                            <Tooltip content={t("common:remove") || "Remove"}>
                              <Button
                                size="xs"
                                color="light"
                                onClick={() =>
                                  onRemoveAgent(agent.id || agent.agent_id)
                                }
                              >
                                <HiTrash className="h-4 w-4 text-red-600 dark:text-red-400" />
                              </Button>
                            </Tooltip>
                          </div>
                        </Table.Cell>
                      )}
                    </Table.Row>
                 );
              })
            ) : (
                <Table.Row>
                  <Table.Cell colSpan={isAdmin ? 5 : 4} className="px-6 py-4 text-center">
                    No agents assigned.
                  </Table.Cell>
                </Table.Row>
            )}
          </Table.Body>
        </Table>
      </div>
      <TablePagination
        pagination={pagination}
        onPageChange={onPageChange}
      />
    </div>
  );
}
